import { useFlow } from "@frigade/react";
import { config } from "@/config";
import { OnboardingItem } from "./OnboardingItem";
import { useAppState } from "@/store/useApp";
import { useEffect } from "react";
import CompleteOnboarding from "./CompleteOnboarding";
import { useOnboardingVisibility } from "./useOnboardingVisibility";

export type OnboardingSteps =
  | "navigate"
  | "highlight"
  | "grading_remark"
  | "ai_suggestion"
  | "generate_report"
  | "publish_feedback"
  | "complete_onboarding";

export interface OnboardingGuideProps {
  className?: string;
}

export const OnboardingGuide = ({ className }: OnboardingGuideProps) => {
  const { flow } = useFlow(config.frigade.flows.gettingStarted);
  const { flow: blockFlow } = useFlow(config.frigade.flows.blockHint);
  const onboarding = useAppState((state) => state.onboarding);
  const shouldRender = useOnboardingVisibility();

  useEffect(() => {
    const currentStep = flow?.getCurrentStep();
    if (currentStep) {
      const completionMap = {
        navigate: onboarding.navigate,
        highlight: onboarding.highlight,
        grading_remark: onboarding.comment,
        ai_suggestion: onboarding.suggestion,
        generate_report: onboarding.generate,
        publish_feedback: onboarding.publish,
        complete_onboarding: false,
      };

      const currentCompleted = completionMap[currentStep.id as OnboardingSteps];

      if (currentCompleted) {
        currentStep.complete();

        if (currentStep.id === "navigate") {
          blockFlow?.complete();
        }
      }
    }
  }, [blockFlow, flow, onboarding]);

  if (!shouldRender || !flow || flow.isCompleted) {
    return null;
  }

  const currentStepIndex = flow.getCurrentStepOrder();
  const completedSteps = flow.getNumberOfCompletedSteps();
  const totalSteps = flow.getNumberOfAvailableSteps() - 1; // Exclude complete onboarding step

  return (
    <div className={`absolute z-50 bottom-5 right-5 ${className}`}>
      <CompleteOnboarding />

      <div
        className={`w-[400px] border-1 border-[#E8EAED] rounded-lg p-4  bg-white z-50`}
      >
        <div className="flex flex-row items-center justify-between space-y-0 pb-2">
          <span className="text-sm font-medium">Getting started</span>
          <div className="bg-[#FF5430] text-white rounded-sm text-xs py-1 px-2">
            {currentStepIndex}/{totalSteps}
          </div>
        </div>
        <OnboardingItem
          completed={completedSteps > 0}
          description="Navigate between Blocks using [↑] / [↓] keys"
        />
        <OnboardingItem
          completed={completedSteps > 1}
          description="Press [A] or [D] to highlight a Block"
        />
        <OnboardingItem
          completed={completedSteps > 2}
          description="Press [N] to add a comment or grading remark"
        />
        <OnboardingItem
          completed={completedSteps > 3}
          description="Press [→] to view and [Tab] to approve AI suggestions"
        />
        <OnboardingItem
          completed={completedSteps > 4}
          description="Open rubric view and generate your report"
        />
        <OnboardingItem
          completed={completedSteps > 5}
          description="Publish your grade and feedback"
        />
      </div>
    </div>
  );
};

export default OnboardingGuide;
